import React from 'react';

import { KnpPaginationData, ValuePair } from 'app/model';

import { DelayedSearch, DelayedSearchProps } from './delayed-search';
import { Translate } from './translate';
import { GeneralConstants } from '../constants/general-constants';
import { Brand } from '../model/cris/brand';
import { Store } from '../model/cris/store';
import { searchService } from '../services/search-service';

type BrandStorePair = ValuePair<Brand | undefined, Store | undefined>;

type FreeSoloMapping<FreeSolo> = FreeSolo extends true ? string : never;
type ValueMapping<T, FreeSolo> = FreeSoloMapping<FreeSolo> | T;

type OwnProps = 'getOptionLabel' | 'isOptionEqualToValue' | 'label' | 'mapDataToOptions' | 'onSearch' | 'value';

interface CrisEntityPickerProps<FreeSolo extends boolean | undefined>
    extends Omit<DelayedSearchProps<BrandStorePair, FreeSolo>, OwnProps> {
    value?: ValueMapping<BrandStorePair, FreeSolo>;
    freeSolo?: FreeSolo;
}

export class CrisEntityPicker<FreeSolo extends boolean | undefined> extends React.Component<
    CrisEntityPickerProps<FreeSolo>
> {
    public render(): React.ReactNode {
        const { value, ...other } = this.props;

        return (
            <DelayedSearch
                {...other}
                value={value !== undefined ? value : GeneralConstants.COMPONENT_VALUE_NULL}
                label={<Translate tkey='forms:Brand or Store Name' />}
                onSearch={(data) => searchService.searchUp(data, 1, GeneralConstants.PAGE_SIZE_LARGE)}
                isOptionEqualToValue={(option, val) => {
                    if (other.freeSolo) {
                        return false;
                    }
                    val = val as BrandStorePair;
                    if (!val || !option || !option.first || !val.first) {
                        return false;
                    }
                    if (option.first.id !== val.first.id) {
                        return false;
                    }

                    if (!option.second || !val.second) {
                        return option.second === val.second; // true only if both undefined
                    }

                    return val.second.id === option.second.id;
                }}
                mapDataToOptions={(data: KnpPaginationData<Brand>) => {
                    const allStores = data.data.map((mapBrand) => {
                        const cleanBrand: Brand = { ...mapBrand, stores: undefined };
                        const results: BrandStorePair[] = [{ first: cleanBrand, second: undefined }];
                        if (!mapBrand.stores) {
                            return results;
                        }

                        results.push(...mapBrand.stores.map((mapStore) => ({ first: cleanBrand, second: mapStore })));

                        return results;
                    });

                    return allStores.reduce((prev, current) => current.concat(prev), []);
                }}
                getOptionLabel={(data) => {
                    if ('string' === typeof data) {
                        return data;
                    }

                    if (!data || !data.first) {
                        return '';
                    }

                    if (!data.second) {
                        return `${data.first.name} [BRAND]`;
                    }

                    return `${data.second.name} (${data.first.name})`;
                }}
            />
        );
    }
}
